import { useEffect, useRef, useState } from "react";
import { Styled } from "./styled";
import useScrollSync from "../useScrollSync";

interface IProps {
  element: HTMLElement | null;
  scrollContentElement?: HTMLElement | null;
  heightOffset?: number;
  onGetWidthScrollVertical?: (width: number) => void;
  isCustom?: boolean;
}

const CustomVerticalScrollBar = (props: IProps) => {
  const {
    element,
    scrollContentElement,
    heightOffset = 0,
    onGetWidthScrollVertical,
    isCustom,
  } = props;

  const [height, setHeight] = useState(0);
  const [scrollHeight, setScrollHeight] = useState(0);

  const containerScrollBar = useRef<HTMLDivElement>(null);
  const handleScrollbarScroll = useScrollSync({
    axis: "vertical",
    element,
    scrollbarRef: containerScrollBar,
  });

  const handleScrollBar = () => {
    handleScrollbarScroll();
  };

  // Observe realtime height + scrollHeight
  useEffect(() => {
    if (!element) return;

    const target = scrollContentElement || element;

    const update = () => {
      setHeight(element.offsetHeight);
      setScrollHeight(target.scrollHeight);
    };

    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(element);
    if (target !== element) resizeObserver.observe(target);

    const mutationObserver = new MutationObserver(update);
    mutationObserver.observe(target, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    update();

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [element, scrollContentElement]);

  const heightContainerElement = Math.max(0, height - heightOffset);
  const heightScrollElement = Math.max(0, scrollHeight - heightOffset);
  const hasScroll = heightScrollElement - heightContainerElement > 0;

  useEffect(() => {
    if (!onGetWidthScrollVertical || !containerScrollBar.current) return;

    if (!hasScroll) {
      onGetWidthScrollVertical(0);
      return;
    }

    const scrollbarElement = containerScrollBar.current;
    const updateWidth = () => {
      onGetWidthScrollVertical(scrollbarElement.offsetWidth - 1);
    };

    const resizeObserver = new ResizeObserver(updateWidth);
    resizeObserver.observe(scrollbarElement);
    updateWidth();

    return () => {
      resizeObserver.disconnect();
    };
  }, [onGetWidthScrollVertical, hasScroll]);
  return (
    <Styled
      ref={containerScrollBar}
      style={{
        height: heightContainerElement,
        width: hasScroll ? "auto" : 1,
      }}
      onScroll={handleScrollBar}
      className={isCustom ? "custom-scroll" : ""}
    >
      {hasScroll && (
        <div
          className="vertical-scroll-bar-inside"
          style={{
            height: heightScrollElement,
          }}
        />
      )}
    </Styled>
  );
};

export default CustomVerticalScrollBar;
